import { useState, useEffect } from 'react'
import { XMarkIcon, DocumentTextIcon, PlusIcon } from '@heroicons/react/24/outline'
import api from '@/services/api'

export default function MemoManagementModal({ 
  isOpen, 
  onClose, 
  user 
}) {
  const [memos, setMemos] = useState([])
  const [newMemo, setNewMemo] = useState('')
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (isOpen && user) {
      fetchMemos()
      setNewMemo('')
      setError('')
    }
  }, [isOpen, user])

  const fetchMemos = async () => {
    try {
      setLoading(true)
      const response = await api.get(`/users/${user.id}/memos?limit=100`)
      
      if (response.data.success) {
        setMemos(response.data.data.memos || [])
      }
    } catch (error) {
      console.error('Failed to fetch memos:', error)
      setError('메모를 불러오는데 실패했습니다.')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!newMemo.trim()) {
      setError('메모 내용을 입력해주세요.')
      return
    }

    setSubmitting(true)
    setError('')

    try {
      await api.post('/users/memo', {
        userId: user.id,
        memo: newMemo.trim()
      })

      setNewMemo('')
      await fetchMemos() // Refresh memos
    } catch (error) {
      console.error('Failed to add memo:', error)
      setError(error.response?.data?.message || '메모 추가에 실패했습니다.')
    } finally {
      setSubmitting(false)
    }
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (!isOpen || !user) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl h-[90vh] max-h-[600px] flex flex-col">
        {/* Header - Fixed */}
        <div className="flex justify-between items-center p-4 sm:p-6 pb-0 flex-shrink-0">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <DocumentTextIcon className="h-5 w-5" />
            메모 관리 - {user.nickname || user.username}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 pt-4">
          {/* User Info */}
          <div className="mb-4 p-3 sm:p-4 bg-gray-50 rounded-lg">
          <h4 className="font-medium text-gray-900 mb-2 text-sm sm:text-base">사용자 정보</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4 text-sm">
            <div>
              <span className="text-gray-600">사용자명:</span>
              <span className="ml-2 font-medium">{user.username}</span>
            </div>
            <div>
              <span className="text-gray-600">닉네임:</span>
              <span className="ml-2 font-medium">{user.nickname}</span>
            </div>
            <div>
              <span className="text-gray-600">이름:</span>
              <span className="ml-2 font-medium">{user.name || 'N/A'}</span>
            </div>
            <div>
              <span className="text-gray-600">가입일:</span>
              <span className="ml-2 font-medium">
                {new Date(user.createdAt).toLocaleDateString('ko-KR')}
              </span>
            </div>
          </div>
          </div>

          {/* Add New Memo */}
          <form onSubmit={handleSubmit} className="mb-4">
          <div className="flex flex-col space-y-3">
            <label className="block text-sm font-medium text-gray-700">
              새 메모 작성
            </label>
            <textarea
              value={newMemo}
              onChange={(e) => setNewMemo(e.target.value)}
              placeholder="이 사용자에 대한 메모를 입력하세요..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              maxLength={1000}
            />
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-500">
                {newMemo.length}/1000
              </span>
              <button
                type="submit"
                disabled={submitting || !newMemo.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
              >
                <PlusIcon className="h-4 w-4" />
                {submitting ? '추가중...' : '메모 추가'}
              </button>
            </div>
          </div>
          </form>

          {error && (
            <div className="mb-4 text-red-600 text-sm bg-red-50 p-3 rounded-md">
              {error}
            </div>
          )}

          {/* Memos List */}
          <div>
            <h4 className="font-medium text-gray-900 mb-3 text-sm sm:text-base">
              기존 메모 ({memos.length}개)
            </h4>
            
            <div className="max-h-[300px] overflow-y-auto space-y-3 pr-2 border border-gray-200 rounded-lg p-3">
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              </div>
            ) : memos.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <DocumentTextIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p>아직 작성된 메모가 없습니다.</p>
              </div>
            ) : (
              memos.map((memo, index) => (
                <div
                  key={memo.id || index}
                  className="bg-gray-50 p-3 sm:p-4 rounded-lg border-l-4 border-blue-500"
                >
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-2 gap-1">
                    <span className="text-xs sm:text-sm font-medium text-gray-900">
                      관리자: {memo.admin?.nickname || memo.admin?.username || 'Unknown'}
                    </span>
                    <span className="text-xs text-gray-500">
                      {formatDate(memo.createdAt)}
                    </span>
                  </div>
                  <p className="text-xs sm:text-sm text-gray-700 whitespace-pre-wrap break-words">
                    {memo.memo}
                  </p>
                </div>
              ))
            )}
            </div>
          </div>
        </div>

        {/* Close Button - Fixed at bottom */}
        <div className="p-4 sm:p-6 pt-4 border-t flex-shrink-0">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  )
}